import { Payment } from '@mui/icons-material';
import { Typography, Button, Box, List, ListItem, ListItemIcon, FormControlLabel, Grid2, Checkbox, Link, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation, Trans } from 'react-i18next';
import { Currencies, CURRENCY_SYMBOLS } from '../../../types';

interface Props {
    total: string;
    paymentIntentId: string;
    clientSecret: string;
    currency: Currencies;
    isMultipleSellers: boolean;
}

const PaymentFormContent = ({ total, currency, isMultipleSellers }: Props) => {
	const stripe = useStripe();
	const elements = useElements();
	const { t } = useTranslation();
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const currencySymbol = CURRENCY_SYMBOLS[currency as Currencies];

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
        
		if (!stripe || !elements) {
			return;
		}

		setIsProcessing(true);
		setError(null);

		try {
			const { error: submitError } = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: `${window.location.origin}/payment/success`,
				},
			});

			if (submitError) {
				setError(submitError.message || 'An error occurred during payment.');
				setIsProcessing(false);
				return;
			}

			// If we get here, the payment was successful
		} catch (err) {
			setError('An unexpected error occurred.');
			setIsProcessing(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<List>
				<Typography variant='h6' sx={{ my: 2 }}>{t('checkout.paymentInformation')}</Typography>
				<ListItem>
					<ListItemIcon>
						<Payment />
					</ListItemIcon>
					<Grid2 container spacing={2}>
						<Grid2 size={12}>
							<PaymentElement />
						</Grid2>
					</Grid2>
				</ListItem>
			</List>

			<Grid2 size={12}>
				<Box>
					<Typography variant='body2' sx={{ my: 2 }}>{t('checkout.paymentAgentNotice', {count: isMultipleSellers ? 2 : 1})}</Typography>
				</Box>
				<FormControlLabel
					name="termsAndPrivacyConsent"
					sx={{ userSelect: 'none' }}
					slotProps={{ typography: { fontSize: '0.9rem' } }}
					control={<Checkbox />}
					required
					label={<Trans i18nKey="checkout.termsAndPrivacyConsentLabel" components={{ 
						privacyPolicyLink: <Link href={t('common.privacyPolicyHref')} target="_blank" />,
						termsOfServiceLink: <Link href={t('common.termsOfServiceHref')} target="_blank" />
					}} />}
				/>
			</Grid2>

			{error && (
				<Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
					{error}
				</Typography>
			)}

			<Box display='flex' justifyContent='center' mt={3}>
				<Button 
					fullWidth 
					size='large' 
					variant='contained' 
					type="submit"
					disabled={!stripe || isProcessing}
				>
					{t('checkout.pay', { total: total, currencySymbol })}
				</Button>
			</Box>
		</form>
	);
};

const PaymentForm = (props: Props) => {
	const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	
	useEffect(() => {
		setIsLoading(true);
		
		// Dynamically import Stripe only when component mounts
		import('@stripe/stripe-js').then(({ loadStripe }) => {
			const promise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');
			setStripePromise(promise);
			setIsLoading(false);
		}).catch((error) => {
			console.error('Failed to load Stripe:', error);
			setIsLoading(false);
		});
	}, []);

	const options = {
		clientSecret: props.clientSecret,
		appearance: {
			theme: 'stripe' as const,
		},
	};

	if (isLoading || !stripePromise) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
				<Typography sx={{ ml: 2 }}>{t('checkout.loadingPaymentForm')}</Typography>
			</Box>
		);
	}

	return (
		<Elements stripe={stripePromise} options={options}>
			<PaymentFormContent {...props} />
		</Elements>
	);
};

export default PaymentForm;
